import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Edit, Eye, Trash2 } from "react-feather";
import Table from "../../pagination/datatable";
import TooltipIcons from "../../common/tooltip-content/tooltipIcons";
import RefreshIcon from "../../common/tooltip-content/refresh";
import CollapesIcon from "../../common/tooltip-content/collapes";
import { PlusCircle } from "feather-icons-react/build/IconComponents";
import StoreServices from "../../../services/StoreServices";
import UserService from "../../../services/UserServices";
import BrandService from "../../../services/BrandService";
import FranchiseServices from "../../../services/FranchiseServices";

const StoreList = () => {
  // State hooks
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    status: "active",
    storeManager: "",
    franchiseOwner: "",
    franchise: "",
    brand: "",
  });

  const [editingStoreId, setEditingStoreId] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    status: "active",
    storeManager: "",
    franchiseOwner: "",
    franchise: "",
    brand: "",
  });

  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // State for API data
  const [brands, setBrands] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [stores, setStores] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);

  // Sequential API calls
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setLoadingError(null);

      try {
        // Step 1: Fetch brands
        const brandsData = await BrandService.getAllBrands();
        if (!brandsData) throw new Error("Failed to fetch brands");
        setBrands(brandsData || []);

        // Get default brand ID
        const defaultBrandId = brandsData?.[0]?._id;
        if (!defaultBrandId) {
          setLoading(false);
          return; // Early return if no brands, prevents unnecessary API calls
        }

        // Step 2: Fetch franchises using the brand ID
        const franchisesData = await FranchiseServices.getBrandFranchises(
          defaultBrandId
        );
        if (!franchisesData) throw new Error("Failed to fetch franchises");
        setFranchises(franchisesData || []);

        // Get default franchise ID
        const defaultFranchiseId = franchisesData?.[0]?._id;
        if (!defaultFranchiseId) {
          setLoading(false);
          return; // Early return if no franchises
        }

        // Step 3: Fetch stores using the franchise ID
        const storesResponse = await StoreServices.getFranchiseStores(
          defaultFranchiseId
        );
        if (!storesResponse) throw new Error("Failed to fetch stores");
        setStores(storesResponse?.data || []);

        // Step 4: Fetch users
        const usersData = await UserService.getAllUsers();
        if (!usersData) throw new Error("Failed to fetch users");
        setUsers(usersData || []);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoadingError(error.message || "Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Get default IDs for form data
  const defaultBrandId = brands?.[0]?._id;
  const defaultFranchiseId = franchises?.[0]?._id;
  // Effects to update form data when dependencies change
  useEffect(() => {
    if (defaultBrandId) {
      setFormData((prev) => ({ ...prev, brand: defaultBrandId }));
      setEditFormData((prev) => ({ ...prev, brand: defaultBrandId }));
    }
  }, [defaultBrandId]);

  useEffect(() => {
    if (defaultFranchiseId) {
      setFormData((prev) => ({ ...prev, franchise: defaultFranchiseId }));
      setEditFormData((prev) => ({ ...prev, franchise: defaultFranchiseId }));
    }
  }, [defaultFranchiseId]);

  // Handlers for form updates
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await StoreServices.createStore(defaultFranchiseId, formData);
      window.location.reload();
    } catch (err) {
      setError(err.message || "Failed to create store");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await StoreServices.updateStore(editingStoreId, editFormData);
      window.location.reload();
    } catch (err) {
      setError(err.message || "Failed to update store");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    setError(null);

    try {
      await StoreServices.deleteStore(editingStoreId);
      window.location.reload();
    } catch (err) {
      setError(err.message || "Failed to delete store");
    } finally {
      setSubmitting(false);
    }
  };

  // Table columns definition
  const columns = [
    {
      title: "Store Name",
      dataIndex: "name",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "Brand",
      dataIndex: "brand",
      render: (_, record) => {
        const brandObj = brands?.find((b) => b._id === record.brand);
        return brandObj?.name || "Not assigned";
      },
      sorter: (a, b) => {
        const brandA =
          brands?.find((brand) => brand._id === a.brand)?.name || "";
        const brandB =
          brands?.find((brand) => brand._id === b.brand)?.name || "";
        return brandA.localeCompare(brandB);
      },
    },
    {
      title: "Franchise Owner",
      dataIndex: "franchiseOwner",
      render: (_, record) => {
        const ownerObj = users?.find((u) => u._id === record.franchiseOwner);
        return ownerObj?.name || "Not assigned";
      },
      sorter: (a, b) => {
        const ownerA =
          users?.find((u) => u._id === a.franchiseOwner)?.name || "";
        const ownerB =
          users?.find((u) => u._id === b.franchiseOwner)?.name || "";
        return ownerA.localeCompare(ownerB);
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      sorter: (a, b) => (a.email || "").localeCompare(b.email || ""),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      sorter: (a, b) => (a.phone || "").localeCompare(b.phone || ""),
    },
    {
      title: "Address",
      dataIndex: "address",
      sorter: (a, b) => (a.address || "").localeCompare(b.address || ""),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => (
        <span
          className={`badge badge-${
            status === "active" ? "success" : "danger"
          } d-inline-flex align-items-center badge-xs`}
        >
          <i className="ti ti-point-filled me-1" />
          {status ? status.charAt(0).toUpperCase() + status.slice(1) : "N/A"}
        </span>
      ),
      sorter: (a, b) => (a.status || "").localeCompare(b.status || ""),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            <div className="input-block add-lists"></div>
            <Link
              className="me-2 p-2"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#view-store"
              onClick={() => {
                setEditingStoreId(record._id);
                setEditFormData({
                  name: record.name || "",
                  email: record.email || "",
                  phone: record.phone || "",
                  address: record.address || "",
                  status: record.status || "active",
                  storeManager: record.storeManager?._id || "",
                  brand: record.brand?._id || "",
                  franchiseOwner: record.franchiseOwner?._id || "",
                  franchise: record.franchise?._id || defaultFranchiseId || "",
                });
              }}
            >
              <Eye className="feather-view" />
            </Link>
            <Link
              className="me-2 p-2"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#edit-store"
              onClick={() => {
                setEditingStoreId(record._id);
                setEditFormData({
                  name: record.name || "",
                  email: record.email || "",
                  phone: record.phone || "",
                  address: record.address || "",
                  status: record.status || "active",
                  storeManager: record.storeManager?._id || "",
                  brand: record.brand?._id || "",
                  franchiseOwner: record.franchiseOwner?._id || "",
                  franchise: record.franchise?._id || defaultFranchiseId || "",
                });
              }}
            >
              <Edit className="feather-edit" />
            </Link>
            <Link
              className="confirm-text p-2"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#delete-modal"
              onClick={() => setEditingStoreId(record._id)}
            >
              <Trash2 className="feather-trash-2" />
            </Link>
          </div>
        </div>
      ),
    },
  ];
  // Main UI render
  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Stores</h4>
                <h6>Manage your stores</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <TooltipIcons />
              <RefreshIcon />
              <CollapesIcon />
            </ul>
            <div className="page-btn">
              <Link
                to="#"
                className="btn btn-primary text-white"
                data-bs-toggle="modal"
                data-bs-target="#add-store"
              >
                <PlusCircle className="me-1" />
                Add Store
              </Link>
            </div>
          </div>

          {/* Store List Table */}
          <div className="card table-list-card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="search-set"></div>
              <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div className="dropdown me-2">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    Status
                  </Link>
                  <ul className="dropdown-menu dropdown-menu-end p-3">
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Active
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Inactive
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>{" "}
            <div className="card-body">
              <div className="table-responsive">
                {loading ? (
                  <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading data...</p>
                  </div>
                ) : loadingError ? (
                  <div className="text-center p-4">
                    <div className="alert alert-danger" role="alert">
                      {loadingError}
                      <button
                        className="btn btn-sm btn-outline-danger ms-3"
                        onClick={() => window.location.reload()}
                      >
                        Retry
                      </button>
                    </div>
                  </div>
                ) : !brands.length ? (
                  <div className="text-center p-4">
                    <p>No brands available. Please create a brand first.</p>
                  </div>
                ) : !franchises.length ? (
                  <div className="text-center p-4">
                    <p>
                      No franchises available for this brand. Please create a
                      franchise first.
                    </p>
                  </div>
                ) : stores?.length === 0 ? (
                  <div className="text-center p-4">
                    <p>No stores found.</p>
                  </div>
                ) : (
                  <Table columns={columns} dataSource={stores} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Store Modal */}
      <div className="modal fade" id="add-store">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <div className="page-title">
                <h4>Add Store</h4>
              </div>
              <button
                type="button"
                className="close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}
                <div className="mb-3">
                  <label className="form-label">
                    Store Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Email <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Phone <span className="text-danger">*</span>
                  </label>
                  <input
                    type="tel"
                    className="form-control"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Address <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Status <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    required
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Brand <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    name="brand"
                    value={formData.brand}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  >
                    <option value="">Select Brand</option>
                    {!loading &&
                      brands?.map((brand) => (
                        <option key={brand._id} value={brand._id}>
                          {brand.name}
                        </option>
                      ))}
                  </select>
                  {loading && (
                    <small className="text-muted">Loading brands...</small>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Franchise Owner <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    name="franchiseOwner"
                    value={formData.franchiseOwner}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Franchise Owner</option>
                    {users
                      ?.filter((user) => user.role === "franchise_owner")
                      .map((user) => (
                        <option key={user._id} value={user._id}>
                          {user.name}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Store Manager</label>
                  <select
                    className="form-control"
                    name="storeManager"
                    value={formData.storeManager}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Store Manager</option>
                    {users?.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                  data-bs-dismiss="modal"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary fs-13 fw-medium p-2 px-3"
                  disabled={submitting}
                >
                  {submitting ? "Creating..." : "Create Store"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Edit Store Modal */}
      <div className="modal fade" id="edit-store">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <div className="page-title">
                <h4>Edit Store</h4>
              </div>
              <button
                type="button"
                className="close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <form onSubmit={handleEdit}>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">
                    Store Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={editFormData.name}
                    onChange={handleEditChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Email <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={editFormData.email}
                    onChange={handleEditChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Phone <span className="text-danger">*</span>
                  </label>
                  <input
                    type="tel"
                    className="form-control"
                    name="phone"
                    value={editFormData.phone}
                    onChange={handleEditChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Address <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    name="address"
                    value={editFormData.address}
                    onChange={handleEditChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Status <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    name="status"
                    value={editFormData.status}
                    onChange={handleEditChange}
                    required
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Brand <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    name="brand"
                    value={editFormData.brand}
                    onChange={handleEditChange}
                    required
                    disabled={loading}
                  >
                    <option value="">Select Brand</option>
                    {!loading &&
                      brands?.map((brand) => (
                        <option key={brand._id} value={brand._id}>
                          {brand.name}
                        </option>
                      ))}
                  </select>
                  {loading && (
                    <small className="text-muted">Loading brands...</small>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Franchise Owner <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    name="franchiseOwner"
                    value={editFormData.franchiseOwner}
                    onChange={handleEditChange}
                    required
                  >
                    <option value="">Select Franchise Owner</option>
                    {users
                      ?.filter((user) => user.role === "franchise_owner")
                      .map((user) => (
                        <option key={user._id} value={user._id}>
                          {user.name}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Store Manager</label>
                  <select
                    className="form-control"
                    name="storeManager"
                    value={editFormData.storeManager}
                    onChange={handleEditChange}
                    required
                  >
                    <option value="">Select Store Manager</option>
                    {users?.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                  data-bs-dismiss="modal"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary fs-13 fw-medium p-2 px-3"
                  disabled={submitting}
                >
                  {submitting ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* View Store Modal */}
      <div className="modal fade" id="view-store">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <div className="page-title">
                <h4>Store Details</h4>
              </div>
              <button
                type="button"
                className="close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <div className="modal-body">
              <div className="info-details">
                <div className="details-list">
                  <div className="customer-details">
                    <span>Store Name:</span>
                    <h6>{editFormData.name}</h6>
                  </div>
                  <div className="customer-details">
                    <span>Email:</span>
                    <h6>{editFormData.email}</h6>
                  </div>
                  <div className="customer-details">
                    <span>Phone:</span>
                    <h6>{editFormData.phone}</h6>
                  </div>
                  <div className="customer-details">
                    <span>Address:</span>
                    <h6>{editFormData.address}</h6>
                  </div>
                  <div className="customer-details">
                    <span>Brand:</span>
                    <h6>
                      {brands?.find((b) => b._id === editFormData.brand)
                        ?.name || "Not assigned"}
                    </h6>
                  </div>
                  <div className="customer-details">
                    <span>Franchise Owner:</span>
                    <h6>
                      {users?.find((u) => u._id === editFormData.franchiseOwner)
                        ?.name || "Not assigned"}
                    </h6>
                  </div>
                  <div className="customer-details">
                    <span>Status:</span>
                    <h6>{editFormData.status}</h6>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <div className="modal fade" id="delete-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content p-5 px-3 text-center">
                <span className="rounded-circle d-inline-flex p-2 bg-danger-transparent mb-2">
                  <i className="ti ti-trash fs-24 text-danger" />
                </span>
                <h4 className="fs-20 text-gray-9 fw-bold mb-2 mt-1">
                  Delete Store
                </h4>
                <p className="text-gray-6 mb-0 fs-16">
                  Are you sure you want to delete this store?
                </p>
                <div className="modal-footer-btn mt-3 d-flex justify-content-center">
                  <button
                    type="button"
                    className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    data-bs-dismiss="modal"
                    className="btn btn-submit fs-13 fw-medium p-2 px-3"
                    onClick={handleDelete}
                  >
                    Yes Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StoreList;
